import { useEffect, useMemo, useRef, useState } from 'react';
import { differenceInCalendarDays, format, isValid, subDays } from 'date-fns';
import { getCommissionReportAll } from '@/services/myCommissionService';
import type { RecaptureRecord } from '@/services/recaptureService';
import { buildRoster, bonusPeriodContribution, recapturePostedInPeriod, type AdminCommissionReport } from './myCommissionReportModel';
import { makeCustom, type ReportPeriod } from './date-range/reportPeriod';

export function previousCommissionWindow(period: ReportPeriod): ReportPeriod | null {
  if (!isValid(period.from) || !isValid(period.to)) {
    return null;
  }
  const days = differenceInCalendarDays(period.to, period.from) + 1;
  if (days <= 0) {
    return null;
  }
  const from = subDays(period.from, days);
  const to = subDays(period.from, 1);
  // Do not manufacture a comparison outside the report's date-only contract.
  return isValid(from) && from.getFullYear() >= 1 ? makeCustom(from, to) : null;
}

export function commissionRosterTrend(current: AdminCommissionReport, prior: AdminCommissionReport,
  records: RecaptureRecord[], period: ReportPeriod, priorPeriod: ReportPeriod): Map<string, number> {
  const activity = new Set(prior.earnings.flatMap(earning => earning.lo_user_id ? [earning.lo_user_id] : []));
  for (const bonus of prior.bonuses ?? []) {
    if (bonusPeriodContribution(bonus, priorPeriod) !== 0) {
      activity.add(bonus.recipient_id);
    }
  }
  const from = format(priorPeriod.from, 'yyyy-MM-dd');
  const to = format(priorPeriod.to, 'yyyy-MM-dd');
  for (const event of prior.volume_bonus_activity ?? []) {
    if (event.date >= from && event.date <= to) {
      activity.add(event.recipient_id);
    }
  }
  for (const record of records) {
    if ((record.status === 'submitted' || record.status === 'reversed') && recapturePostedInPeriod(record, priorPeriod)) {
      activity.add(record.recipient_id);
    }
  }
  const before = new Map(buildRoster(prior.report_users, prior.earnings, {
    records, period: priorPeriod,
    bonuses: prior.bonuses, volumeBonusActivity: prior.volume_bonus_activity
  }).map(row => [row.user_id, row.loProfitability]));
  return new Map(buildRoster(current.report_users, current.earnings, {
    records, period, bonuses: current.bonuses,
    volumeBonusActivity: current.volume_bonus_activity
  }).filter(row => activity.has(row.user_id) && before.has(row.user_id))
    .map(row => [row.user_id, row.loProfitability - before.get(row.user_id)!]));
}

/** A deferred comparison read never controls the main report's loading or error state. */
export function useCommissionRosterTrend({ report, period, records, ready }: {
  report: AdminCommissionReport;
  period: ReportPeriod;
  records: RecaptureRecord[] | null;
  ready: boolean;
}): Map<string, number> | null {
  const generation = useRef(0);
  const [prior, setPrior] = useState<{ report: AdminCommissionReport; period: ReportPeriod; current: AdminCommissionReport } | null>(null);
  useEffect(() => {
    const requests = generation;
    const request = ++requests.current;
    setPrior(null);
    const window = previousCommissionWindow(period);
    if (ready && window) {
      void getCommissionReportAll(format(window.from, 'yyyy-MM-dd'), format(window.to, 'yyyy-MM-dd')).then(result => {
        if (request === requests.current) {
          setPrior({ report: result, period: window, current: report });
        }
      }).catch(() => {
        // Comparison absence is honest; the current-period profitability column stays visible.
        if (request === requests.current) {
          setPrior(null);
        }
      });
    }
    return () => {
      requests.current++;
    };
  }, [report, period, ready]);
  return useMemo(() => ready && records !== null && prior?.current === report
    ? commissionRosterTrend(report, prior.report, records, period, prior.period) : null, [ready, records, prior, report, period]);
}
